import { sendToastMessage } from "@microbeta/ui";
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAnalytics } from "../../../../shared/hooks";
import { httpClient } from "../../../../shared/utils/httpClientInstance";
import { paths } from "../../../../shared/utils/routing";
import { useAuthContext } from "../../AuthContext";

const GoogleCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuthState } = useAuthContext();
  const { captureEvent } = useAnalytics();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const idToken = searchParams.get("idToken");
    const refreshToken = searchParams.get("refreshToken");
    const email = searchParams.get("email");
    const displayName = searchParams.get("displayName");
    const expiresIn = searchParams.get("expiresIn");
    const userId = searchParams.get("userId");
    const isNewUser = searchParams.get("isNewUser") === "true";
    let redirectUrl = searchParams.get("redirectUrl");

    if (!redirectUrl) {
      redirectUrl = sessionStorage.getItem("redirectUrl");
    }

    // Handle case where redirectUrl might be serialized as [object Object]
    if (redirectUrl === "[object Object]" || redirectUrl === "null") {
      redirectUrl = null;
    }

    if (redirectUrl) {
      sessionStorage.removeItem("redirectUrl");
    }

    if (idToken && refreshToken) {
      const callbackData = {
        idToken,
        refreshToken,
        displayName: displayName || "",
        email: email || "",
        expiresIn: expiresIn || "",
        emailVerified: true, //always true since google is verified login method
        userId: userId || "",
        signInMethod: "google",
        loginTime: Date.now(),
      };

      // Set auth state and token before any navigation
      httpClient.setAuthToken(idToken);
      setAuthState(callbackData);

      // Track successful Google sign in/up
      const baseEventData = {
        method: "google",
        email_domain: email?.split("@")[1] || "unknown",
        email_verified: true,
        display_name: displayName || "",
      };

      if (isNewUser) {
        captureEvent("user_signed_up", baseEventData);
      } else {
        captureEvent("user_signed_in", baseEventData);
      }

      // Navigate to onboarding for new users, home for existing users
      if (isNewUser) {
        const onboardingUrl = redirectUrl
          ? `${paths.onboarding}?redirectUrl=${encodeURIComponent(redirectUrl)}`
          : paths.onboarding;
        setTimeout(() => navigate(onboardingUrl), 100);
      } else {
        const finalRedirectUrl = redirectUrl || paths.home;
        setTimeout(() => navigate(finalRedirectUrl), 100);
      }
    } else {
      // Track failed Google sign in
      captureEvent("user_signin_failed", {
        method: "google",
        error_type: "callback_missing_tokens",
      });

      sendToastMessage(
        "Sign In Error",
        "There was a problem signing in. Please try again.",
        "error",
        0
      );
      navigate(paths.signin);
    }
  }, [navigate, searchParams, setAuthState, captureEvent]);

  return <div />;
};

export default GoogleCallback;
